import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const About = () => {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About House of Deepak",
    "description": "Learn about House of Deepak - Where tradition meets contemporary elegance. Our story, values, and commitment to quality fashion.",
    "url": "https://houseofdeepak.in/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "House of Deepak",
      "url": "https://houseofdeepak.in",
      "description": "Curating traditional Indian and contemporary Western fashion with attention to detail and quality."
    }
  };

  return (
    <>
    <SEO 
      title="About Us"
      description="Learn about House of Deepak - Where tradition meets contemporary elegance. Discover our story, values, and commitment to quality fashion. Curating Indian ethnic wear and Western fashion since our inception."
      keywords="about House of Deepak, our story, fashion brand, ethnic wear brand, quality fashion, Scan by Deepak Raj"
      schema={aboutSchema}
    />
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-serif font-light text-deep-brown mb-6">
                About House of Deepak
              </h1>
              <p className="text-xl text-warm-gray max-w-2xl mx-auto leading-relaxed">
                Where tradition meets contemporary elegance
              </p>
            </div>

            {/* Story Section */}
            <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-serif font-light text-deep-brown mb-8 text-center">
                Our Story
              </h2>
              <div className="space-y-6 text-warm-gray leading-relaxed">
                <p>
                  House of Deepak was born from a passion for creating clothing that bridges the gap between traditional craftsmanship and contemporary style. Founded with a vision to celebrate the rich heritage of Indian fashion while embracing modern sensibilities, we curate collections that speak to the modern woman who values both tradition and innovation.
                </p>
                <p>
                  Our journey began with a simple belief: that clothing should be more than just fabric and thread. It should tell a story, evoke emotions, and make the wearer feel confident and beautiful. This philosophy drives everything we do, from the selection of materials to the final stitch.
                </p>
                <p>
                  Today, House of Deepak encompasses two distinct yet harmonious brands: our traditional Indian wear collection and Scan by Deepak Raj, our contemporary western wear line. Each piece is carefully crafted with attention to detail, quality, and style.
                </p>
              </div>
            </div>

            {/* Two Brands Section */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <motion.div
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-8"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-2xl font-serif font-light text-amber-900 mb-4">
                  House of Deepak
                </h3>
                <p className="text-amber-900/80 leading-relaxed">
                  Our traditional Indian wear collection celebrates the timeless beauty of ethnic fashion. From elegant sarees to contemporary kurtas, each piece is designed to honor tradition while embracing modern fits and styles.
                </p>
              </motion.div>

              <motion.div
                className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-8"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-2xl font-serif font-light text-slate-900 mb-4">
                  Scan by Deepak Raj
                </h3>
                <p className="text-slate-900/80 leading-relaxed">
                  Our western wear line offers contemporary fashion for the modern woman. From chic dresses to sophisticated separates, Scan by Deepak Raj combines international trends with impeccable craftsmanship.
                </p>
              </motion.div>
            </div>

            {/* Values Section */}
            <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-serif font-light text-deep-brown mb-8 text-center">
                Our Values
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-deep-brown/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-deep-brown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-deep-brown mb-2">Quality</h4>
                  <p className="text-warm-gray text-sm">
                    We use only the finest materials and employ skilled artisans to ensure every piece meets our high standards.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-deep-brown/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-deep-brown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-deep-brown mb-2">Passion</h4>
                  <p className="text-warm-gray text-sm">
                    Every design is created with love and dedication, reflecting our passion for beautiful, wearable art.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-deep-brown/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-deep-brown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-deep-brown mb-2">Community</h4>
                  <p className="text-warm-gray text-sm">
                    We believe in building a community of confident women who celebrate their unique style and heritage.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="text-center">
              <h2 className="text-2xl font-serif font-light text-deep-brown mb-4">
                Get in Touch
              </h2>
              <p className="text-warm-gray mb-8">
                We'd love to hear from you. Connect with us to learn more about our collections.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:info@houseofdeepak.in"
                  className="btn-primary"
                >
                  Email Us
                </a>
                <a
                  href="https://www.instagram.com/houseofdeepak"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  Follow on Instagram
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
    </>
  );
};

export default About;